import { CommonModule } from '@angular/common';
import { Component, HostListener, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { DirectusService } from '../core/services/directus.service';

type DirectusRecord = Record<string, unknown>;

interface EventRow {
  id: string;
  name: string;
  guests: number;
  salesLabel: string;
  salesValue: number;
  startDate: Date | null;
  startDateLabel: string;
  location: string;
  lastModifiedLabel: string;
  categoryLabel: string;
  categoryId: string;
  status: string;
  eventType: string;
  imageUrl: string;
}

@Component({
  selector: 'app-events-list-view',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="events-page">
      <header class="events-top">
        <h1>{{ heading }}</h1>
        <div class="actions">
          <button type="button" class="btn btn-primary" (click)="openEventWizard()">+ Add Event</button>
        </div>
      </header>

      <section class="table-panel">
        <div class="panel-head">
          <h2>Events ({{ filteredRows.length }})</h2>
          <div class="controls">
            <button type="button" class="btn filter-btn" (click)="openFilterDrawer()">
              Filter{{ activeFilterCount > 0 ? ' (' + activeFilterCount + ')' : '' }}
            </button>
            <button type="button" class="btn filter-btn" (click)="openCustomizeDrawer()">Customize</button>
            <input
              type="text"
              placeholder="Search..."
              [(ngModel)]="search"
              (ngModelChange)="applyFilters()"
            />
          </div>
        </div>

        <div class="active-filter" *ngIf="activeFilterCount > 0">
          <span class="chip" *ngIf="eventStatusFilter !== 'all'">
            Event status: {{ eventStatusFilter | titlecase }}
            <button type="button" (click)="eventStatusFilter = 'all'; applyFilters()">×</button>
          </span>
          <span class="chip" *ngIf="eventTypeFilter !== 'all'">
            Type: {{ eventTypeFilter | titlecase }}
            <button type="button" (click)="eventTypeFilter = 'all'; applyFilters()">×</button>
          </span>
          <span class="chip" *ngIf="categoryFilter !== 'all'">
            Category: {{ categoryFilterLabel }}
            <button type="button" (click)="categoryFilter = 'all'; applyFilters()">×</button>
          </span>
          <button type="button" class="clear" (click)="clearFilters()">Clear</button>
        </div>

        <p *ngIf="loading" class="state">Loading events...</p>
        <p *ngIf="error" class="state error">{{ error }}</p>
        <p *ngIf="!loading && !error && filteredRows.length === 0" class="state">No events found.</p>

        <div class="table-wrap" *ngIf="!loading && !error && filteredRows.length > 0">
          <table>
            <thead>
              <tr>
                <th class="name-col" *ngFor="let col of visibleColumns">{{ col.label }}</th>
                <th class="actions-col"></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of filteredRows" class="event-row">
                <td *ngFor="let col of visibleColumns" [class.name-cell]="col.key === 'name'">
                  <ng-container [ngSwitch]="col.key">
                    <ng-container *ngSwitchCase="'name'">
                      <img [src]="row.imageUrl" alt="" />
                      <button type="button" class="name-link" (click)="openEvent(row.id)">{{ row.name }}</button>
                    </ng-container>
                    <ng-container *ngSwitchCase="'guests'">{{ row.guests }}</ng-container>
                    <ng-container *ngSwitchCase="'sales'">{{ row.salesLabel }}</ng-container>
                    <ng-container *ngSwitchCase="'startDate'">{{ row.startDateLabel }}</ng-container>
                    <ng-container *ngSwitchCase="'location'">{{ row.location }}</ng-container>
                    <ng-container *ngSwitchCase="'lastModified'">{{ row.lastModifiedLabel }}</ng-container>
                    <ng-container *ngSwitchCase="'category'">{{ row.categoryLabel }}</ng-container>
                    <ng-container *ngSwitchCase="'status'">{{ row.status }}</ng-container>
                  </ng-container>
                </td>
                <td class="actions-cell" [class.menu-open]="openEventMenuId === row.id">
                  <button type="button" class="manage-btn" (click)="openEvent(row.id)">Manage</button>
                  <div class="menu-wrap">
                    <button type="button" class="icon-btn" (click)="toggleEventMenu(row.id, $event)">…</button>
                    <div
                      class="row-menu"
                      [style.top.px]="eventMenuTop"
                      [style.left.px]="eventMenuLeft"
                      *ngIf="openEventMenuId === row.id"
                      (click)="$event.stopPropagation()"
                    >
                      <button type="button" (click)="duplicateAsDraft(row)">Duplicate as draft</button>
                      <button type="button" class="danger" (click)="cancelEvent(row)">Cancel event</button>
                      <button type="button" class="danger" (click)="deleteEvent(row)">Delete event</button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <aside class="drawer unified-drawer" *ngIf="filterOpen" [style.top.px]="drawerTop" [style.height]="'calc(100vh - ' + drawerTop + 'px)'">
        <header>
          <h3>Filter</h3>
          <button type="button" class="close" (click)="filterOpen = false">×</button>
        </header>

        <div class="drawer-body">
          <section class="drawer-section section-plain">
            <div class="section-title">
              <h4>Events</h4>
              <span>▾</span>
            </div>
            <div class="options">
              <label class="option-line plain"><input type="radio" name="eventType" value="all" [(ngModel)]="eventTypeFilter" (change)="applyFilters()"> <span>All</span></label>
              <label class="option-line plain"><input type="radio" name="eventType" value="recurring" [(ngModel)]="eventTypeFilter" (change)="applyFilters()"> <span>Recurring</span></label>
              <label class="option-line plain"><input type="radio" name="eventType" value="single" [(ngModel)]="eventTypeFilter" (change)="applyFilters()"> <span>Single</span></label>
            </div>
          </section>

          <section class="drawer-section section-plain">
            <div class="section-title">
              <h4>Event status {{ eventStatusFilter !== 'all' ? '(1)' : '' }}</h4>
              <span>▾</span>
            </div>
            <div class="options">
              <label class="option-line plain"><input type="radio" name="eventStatus" value="all" [(ngModel)]="eventStatusFilter" (change)="applyFilters()"> <span>All</span></label>
              <label class="option-line plain"><input type="radio" name="eventStatus" value="upcoming" [(ngModel)]="eventStatusFilter" (change)="applyFilters()"> <span>Upcoming</span></label>
              <label class="option-line plain"><input type="radio" name="eventStatus" value="past" [(ngModel)]="eventStatusFilter" (change)="applyFilters()"> <span>Past</span></label>
              <label class="option-line plain"><input type="radio" name="eventStatus" value="canceled" [(ngModel)]="eventStatusFilter" (change)="applyFilters()"> <span>Canceled</span></label>
            </div>
          </section>

          <section class="drawer-section section-plain">
            <div class="section-title">
              <h4>Event category</h4>
              <span>▾</span>
            </div>
            <select [(ngModel)]="categoryFilter" (ngModelChange)="applyFilters()">
              <option value="all">Select...</option>
              <option *ngFor="let option of categoryOptions" [value]="option.id">{{ option.name }}</option>
            </select>
          </section>
        </div>
        <footer class="filter-footer">
          <span>{{ activeFilterCount }} filter applied</span>
          <button type="button" (click)="clearFilters()">Clear</button>
        </footer>
      </aside>

      <aside class="drawer unified-drawer" *ngIf="customizeOpen" [style.top.px]="drawerTop" [style.height]="'calc(100vh - ' + drawerTop + 'px)'">
        <header>
          <div>
            <h3>Customize columns</h3>
            <p>Select which columns to show, or drag them into a different order.</p>
          </div>
          <button type="button" class="close" (click)="customizeOpen = false">×</button>
        </header>

        <div class="drawer-body">
          <section class="drawer-section section-plain no-border">
            <div class="search-columns">
              <input
                type="text"
                placeholder="Search columns..."
                [(ngModel)]="columnSearch"
              />
            </div>
          </section>

          <section class="drawer-section section-plain no-border">
            <div class="column-list">
              <label class="column-row" *ngFor="let col of filteredCustomizeColumns">
                <span class="drag">⋮⋮</span>
                <input
                  type="checkbox"
                  [checked]="col.visible"
                  [disabled]="col.key === 'name'"
                  (change)="toggleColumn(col.key)"
                >
                <span class="col-label">{{ col.label }}</span>
                <small *ngIf="col.key === 'name'">Required</small>
              </label>
            </div>
          </section>
        </div>
      </aside>

      <div class="confirm-overlay" *ngIf="confirmDialogOpen" (click)="closeConfirmDialog()">
        <article class="confirm-dialog" (click)="$event.stopPropagation()">
          <header class="confirm-head">
            <h3>{{ confirmDialogTitle }}</h3>
            <button type="button" class="close-btn" (click)="closeConfirmDialog()">×</button>
          </header>
          <p class="confirm-message">{{ confirmDialogMessage }}</p>
          <div class="confirm-actions">
            <button type="button" class="btn filter-btn" (click)="closeConfirmDialog()" [disabled]="confirmDialogBusy">
              Cancel
            </button>
            <button
              type="button"
              class="btn danger-solid"
              (click)="confirmDialogProceed()"
              [disabled]="confirmDialogBusy"
            >
              {{ confirmDialogBusy ? 'Processing...' : confirmDialogConfirmLabel }}
            </button>
          </div>
        </article>
      </div>
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
      .events-page {
        display: grid;
        gap: 14px;
        width: 100%;
        max-width: 1320px;
        margin: 0;
        position: relative;
        min-width: 0;
      }
      .events-top {
        display: flex;
        align-items: end;
        justify-content: space-between;
        gap: 14px;
      }
      h1 {
        margin: 0;
        font-size: 2rem;
        line-height: 1.15;
        letter-spacing: -0.02em;
        font-weight: 700;
      }
      .actions {
        display: flex;
        gap: 0.55rem;
      }
      .btn {
        border: 1px solid #b7dff0;
        background: #f4fbff;
        color: #0097d7;
        border-radius: 999px;
        padding: 0.45rem 0.9rem;
        font-weight: 700;
        cursor: pointer;
      }
      .btn-primary {
        background: #0097d7;
        color: #ffffff;
        border-color: #0097d7;
      }
      .table-panel {
        border: 1px solid #dbe3ee;
        border-radius: 14px;
        background: #ffffff;
        overflow: hidden;
        box-shadow: 0 4px 14px rgba(18, 31, 51, 0.05);
      }
      .panel-head {
        padding: 16px 18px;
        border-bottom: 1px solid #e2e8f0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 0.8rem;
      }
      h2 {
        margin: 0;
        font-size: 1.5rem;
        line-height: 1.1;
        letter-spacing: -0.02em;
        font-weight: 700;
      }
      .controls {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 0.5rem;
        min-width: 0;
        flex: 1;
      }
      .filter-btn {
        background: #f8fafc;
      }
      .controls input {
        border: 1px solid #cbd5e1;
        border-radius: 999px;
        padding: 0.44rem 0.72rem;
        min-width: 160px;
        flex: 0 1 240px;
      }
      .active-filter {
        padding: 0.72rem 1rem 0.4rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      .chip {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        border-radius: 999px;
        background: #e6f4fb;
        color: #1e3a8a;
        padding: 0.22rem 0.55rem;
      }
      .chip button,
      .clear {
        border: 0;
        background: transparent;
        color: inherit;
        cursor: pointer;
        font-weight: 700;
      }
      .state {
        margin: 0;
        padding: 0.85rem 1rem;
        color: #475569;
      }
      .error {
        color: #b91c1c;
      }
      .table-wrap {
        overflow-x: auto;
        overflow-y: visible;
        position: relative;
        max-width: 100%;
        min-width: 0;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        table-layout: auto;
      }
      th {
        text-align: left;
        background: #e6f4fb;
        color: #1e293b;
        padding: 11px 12px;
        font-weight: 600;
      }
      td {
        padding: 13px 12px;
        border-top: 1px solid #e2e8f0;
        color: #1e293b;
        vertical-align: middle;
      }
      .name-col {
        width: 240px;
      }
      .actions-col {
        width: 90px;
        position: sticky;
        right: 0;
        z-index: 3;
        background: #e6f4fb;
      }
      .name-cell {
        display: flex;
        align-items: center;
        gap: 0.55rem;
      }
      .name-cell img {
        width: 56px;
        height: 38px;
        border-radius: 6px;
        object-fit: cover;
        border: 1px solid #dbe3ee;
      }
      .name-link {
        border: 0;
        background: transparent;
        color: #0f172a;
        text-align: left;
        font: inherit;
        font-weight: 700;
        font-size: 1rem;
        line-height: 1.2;
        letter-spacing: 0;
        cursor: pointer;
        padding: 0;
      }
      .name-link:hover {
        color: #0097d7;
      }
      .actions-cell {
        text-align: right;
        white-space: nowrap;
        position: sticky;
        right: 0;
        z-index: 2;
        background: #ffffff;
      }
      .actions-cell.menu-open {
        z-index: 40;
      }
      .manage-btn {
        opacity: 0;
        visibility: hidden;
        border: 1px solid #8fcde7;
        background: #fff;
        color: #0097d7;
        border-radius: 999px;
        padding: 6px 14px;
        font-size: 13px;
        font-weight: 700;
        margin-right: 0.35rem;
        cursor: pointer;
        transition: opacity 0.12s ease;
      }
      .event-row:hover .manage-btn,
      .event-row:focus-within .manage-btn {
        opacity: 1;
        visibility: visible;
      }
      .menu-wrap {
        position: static;
        display: inline-block;
      }
      .icon-btn {
        width: 32px;
        height: 32px;
        border: 1px solid #b7dff0;
        border-radius: 999px;
        background: #ffffff;
        color: #0097d7;
        cursor: pointer;
      }
      .row-menu {
        position: fixed;
        min-width: 150px;
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        background: #ffffff;
        box-shadow: 0 10px 22px rgba(15, 23, 42, 0.14);
        z-index: 30;
        padding: 0.24rem;
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
      }
      .row-menu button {
        width: 100%;
        display: block;
        text-align: left;
        border: 0;
        background: transparent;
        color: #0f172a;
        padding: 0.42rem 0.5rem;
        border-radius: 8px;
        cursor: pointer;
      }
      .row-menu button:hover {
        background: #f4fbff;
      }
      .row-menu .danger {
        color: #b91c1c;
      }
      .drawer {
        position: fixed;
        right: 0;
        width: var(--filter-drawer-width);
        background: #ffffff;
        border-left: 1px solid #dbe3ee;
        border-top: 1px solid #dbe3ee;
        border-radius: 0;
        z-index: 70;
        overflow: hidden;
        display: grid;
        grid-template-rows: auto 1fr;
        box-shadow: 0 8px 22px rgba(15, 23, 42, 0.16);
      }
      .drawer header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 0.72rem 0.78rem 0.62rem;
        border-bottom: 1px solid #e2e8f0;
        background: #ffffff;
      }
      .drawer header p {
        margin: 0.2rem 0 0;
        color: #64748b;
      }
      .drawer h3 {
        margin: 0;
        font-size: 0.95rem;
        font-weight: 700;
      }
      .drawer-body {
        padding: 0;
        display: grid;
        gap: 0;
        align-content: start;
        grid-auto-rows: max-content;
        overflow-y: auto;
      }
      .close {
        border: 0;
        background: transparent;
        font-size: 1.5rem;
        cursor: pointer;
        color: #334155;
      }
      .drawer-section {
        padding: 0.72rem 0.78rem;
        display: grid;
        gap: 0.34rem;
        border-bottom: 1px solid #e2e8f0;
      }
      .section-plain {
        background: #ffffff;
      }
      .no-border {
        border-bottom: 0;
      }
      .drawer-section h4 {
        margin: 0;
        font-size: 1rem;
        line-height: 1.25;
        font-weight: 700;
      }
      .drawer-section p {
        margin: 0;
        color: #64748b;
      }
      .section-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
      }
      .section-title span {
        color: #334155;
        font-size: 0.9rem;
      }
      .options {
        display: grid;
        gap: 0.02rem;
      }
      .drawer-section label {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        justify-content: flex-start;
      }
      .option-line {
        border: 0;
        border-radius: 0;
        padding: 0.02rem 0;
        background: #ffffff;
        min-height: auto;
      }
      .option-line.plain {
        justify-content: flex-start;
      }
      .option-line input {
        margin: 0;
      }
      .option-line span {
        color: #1e293b;
        font-weight: 400;
        line-height: 1.15;
        font-size: 1rem;
      }
      .drawer-section select {
        border: 1px solid #cbd5e1;
        border-radius: 999px;
        padding: 0.2rem 0.56rem;
        width: 100%;
        background: #ffffff;
        font-size: 0.96rem;
        height: var(--filter-control-height);
        min-height: var(--filter-control-height);
        box-sizing: border-box;
      }
      .search-columns input {
        width: 100%;
        border: 1px solid #b7dff0;
        border-radius: 999px;
        padding: 0.45rem 0.75rem;
        background: #ffffff;
        height: var(--filter-control-height);
        min-height: var(--filter-control-height);
        box-sizing: border-box;
      }
      .column-list {
        border-left: 2px solid #0097d7;
      }
      .column-row {
        display: grid;
        grid-template-columns: 18px 22px 1fr auto;
        align-items: center;
        gap: 0.55rem;
        padding: 0.45rem 0.55rem;
        border-bottom: 1px solid #e2e8f0;
      }
      .column-row:last-child {
        border-bottom: 0;
      }
      .drag {
        color: #0097d7;
        letter-spacing: -1px;
        font-size: 0.9rem;
        user-select: none;
      }
      .col-label {
        font-weight: 500;
      }
      .column-row small {
        color: #64748b;
        font-size: 0.76rem;
      }
      .filter-footer {
        border-top: 1px solid #e2e8f0;
        padding: 0.7rem 0.78rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 0.82rem;
        font-weight: 600;
        background: #ffffff;
      }
      .filter-footer button {
        border: 0;
        background: transparent;
        color: #0097d7;
        cursor: pointer;
        font-weight: 600;
      }
      .confirm-overlay {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.36);
        display: grid;
        place-items: center;
        z-index: 200;
        padding: 1rem;
      }
      .confirm-dialog {
        width: min(420px, 100%);
        border: 1px solid #dbe3ee;
        border-radius: 12px;
        background: #ffffff;
        box-shadow: 0 20px 40px rgba(15, 23, 42, 0.24);
        overflow: hidden;
      }
      .confirm-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        padding: 0.8rem 0.9rem;
        border-bottom: 1px solid #e2e8f0;
      }
      .confirm-head h3 {
        margin: 0;
        font-size: 1rem;
        color: #0f172a;
      }
      .close-btn {
        border: 0;
        background: transparent;
        color: #475569;
        cursor: pointer;
        font-size: 1.1rem;
      }
      .confirm-message {
        margin: 0;
        padding: 0.9rem;
        color: #334155;
        font-size: 0.84rem;
        line-height: 1.45;
      }
      .confirm-actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.45rem;
        padding: 0 0.9rem 0.9rem;
      }
      .danger-solid {
        background: #dc2626;
        color: #ffffff;
        border-color: #dc2626;
      }
      @media (max-width: 900px) {
        .events-top {
          flex-direction: column;
          align-items: flex-start;
        }
        h1 {
          font-size: 1.6rem;
        }
        h2 {
          font-size: 1.3rem;
        }
        .actions {
          width: 100%;
          flex-wrap: wrap;
        }
        input {
          min-width: 140px;
        }
        .drawer {
          width: min(340px, 94vw);
        }
        .name-link {
          font-size: 22px;
          line-height: 1.15;
        }
      }
    `
  ]
})
export class EventsListViewComponent implements OnInit {
  @Input({ required: true }) heading = '';
  @Input({ required: true }) status: 'published' | 'draft' = 'published';
  @Input() defaultUpcomingOnly = true;

  rows: EventRow[] = [];
  filteredRows: EventRow[] = [];
  categoryOptions: Array<{ id: string; name: string }> = [];
  loading = false;
  error = '';
  search = '';
  filterOpen = false;
  customizeOpen = false;
  drawerTop = 96;
  columnSearch = '';
  eventTypeFilter: 'all' | 'recurring' | 'single' = 'all';
  eventStatusFilter: 'all' | 'upcoming' | 'past' | 'canceled' = 'all';
  categoryFilter = 'all';
  openEventMenuId: string | null = null;
  openEventMenuDirection: 'up' | 'down' = 'down';
  eventMenuTop = 0;
  eventMenuLeft = 0;
  confirmDialogOpen = false;
  confirmDialogTitle = '';
  confirmDialogMessage = '';
  confirmDialogConfirmLabel = 'Confirm';
  confirmDialogBusy = false;
  private pendingConfirmAction: 'cancel-event' | 'delete-event' | null = null;
  private pendingConfirmEventRow: EventRow | null = null;

  columns: Array<{ key: 'name' | 'guests' | 'sales' | 'startDate' | 'location' | 'lastModified' | 'category' | 'status'; label: string; visible: boolean }> = [
    { key: 'name', label: 'Name', visible: true },
    { key: 'guests', label: 'Guests', visible: true },
    { key: 'sales', label: 'Sales', visible: true },
    { key: 'startDate', label: 'Start date', visible: true },
    { key: 'location', label: 'Location', visible: true },
    { key: 'lastModified', label: 'Last modified', visible: true },
    { key: 'category', label: 'Category', visible: true },
    { key: 'status', label: 'Status', visible: false }
  ];

  private readonly directusBase = environment.directusUrl.replace(/\/$/, '');
  private readonly displayTimezone = 'Asia/Jakarta';

  constructor(
    private readonly directus: DirectusService,
    private readonly router: Router
  ) {}

  @HostListener('document:click')
  handleDocumentClick(): void {
    this.openEventMenuId = null;
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    this.updateDrawerOffset();
  }

  async ngOnInit(): Promise<void> {
    this.eventStatusFilter = this.defaultUpcomingOnly ? 'upcoming' : 'all';
    this.updateDrawerOffset();
    await this.loadRows();
  }

  openFilterDrawer(): void {
    this.updateDrawerOffset();
    this.filterOpen = true;
  }

  openCustomizeDrawer(): void {
    this.updateDrawerOffset();
    this.customizeOpen = true;
  }

  private updateDrawerOffset(): void {
    const topBanner = document.querySelector('.top') as HTMLElement | null;
    if (!topBanner) {
      this.drawerTop = 96;
      return;
    }
    this.drawerTop = Math.max(0, Math.round(topBanner.getBoundingClientRect().bottom));
  }

  async loadRows(): Promise<void> {
    this.loading = true;
    this.error = '';

    try {
      const [events, attendeesNew, orders, categories] = await Promise.all([
        this.safeList('events', {
          fields: 'id,title,start_date,location,status,event_type,eventcategory_id,image,created_at',
          ...this.eventStatusQuery(),
          sort: '-start_date',
          limit: 200
        }),
        this.safeList('event_attendees', { fields: 'id,event_id', limit: 1000 }),
        this.safeList('event_orders', {
          fields: 'id,event_id,status,grand_total',
          'filter[status][_eq]': 'paid',
          limit: 1000
        }),
        this.safeList('eventcategories', { fields: 'id,name', limit: 500 })
      ]);

      const guestCountByEvent = new Map<string, number>();
      const attendeeSource = attendeesNew;
      attendeeSource.forEach((row) => {
        const eventId = String(row['event_id'] ?? '');
        if (!eventId) return;
        guestCountByEvent.set(eventId, (guestCountByEvent.get(eventId) || 0) + 1);
      });

      const salesByEvent = new Map<string, number>();
      orders.forEach((row) => {
        const eventId = String(row['event_id'] ?? '');
        if (!eventId) return;
        const raw = row['grand_total'];
        const value = typeof raw === 'number' ? raw : Number(raw);
        salesByEvent.set(eventId, (salesByEvent.get(eventId) || 0) + (Number.isFinite(value) ? value : 0));
      });

      const categoryById = new Map<string, string>();
      categories.forEach((row) => {
        const id = String(row['id'] ?? '');
        if (!id) return;
        const name = typeof row['name'] === 'string' && row['name'].trim() ? row['name'] : `Category ${id}`;
        categoryById.set(id, name);
      });
      this.categoryOptions = Array.from(categoryById.entries()).map(([id, name]) => ({ id, name }));

      this.rows = events.map((event) => this.mapEvent(event, guestCountByEvent, salesByEvent, categoryById));
      this.applyFilters();
      if (events.length === 0) this.error = '';
    } finally {
      this.loading = false;
    }
  }

  private eventStatusQuery(): Record<string, string> {
    if (this.status === 'draft') {
      return { 'filter[status][_eq]': 'draft' };
    }

    // Published list should include ended/cancelled records that were previously published.
    return { 'filter[status][_in]': 'published,closed,cancelled,canceled' };
  }

  private mapEvent(
    event: DirectusRecord,
    guestCountByEvent: Map<string, number>,
    salesByEvent: Map<string, number>,
    categoryById: Map<string, string>
  ): EventRow {
    const id = String(event['id'] ?? '');
    const start = typeof event['start_date'] === 'string' ? new Date(event['start_date']) : null;
    const categoryId = event['eventcategory_id'];
    const categoryLabel = categoryId !== null && categoryId !== undefined
      ? categoryById.get(String(categoryId)) || String(categoryId)
      : '—';

    const imageId = this.extractFileId(event['image']);
    const imageUrl = imageId
      ? this.directus.buildAssetUrl(imageId, { width: 144, height: 96, fit: 'cover' })
      : '';

    const salesValue = salesByEvent.get(id) || 0;

    return {
      id,
      name: typeof event['title'] === 'string' && event['title'].trim() ? event['title'] : 'Untitled event',
      guests: guestCountByEvent.get(id) || 0,
      salesValue,
      salesLabel: new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(salesValue),
      startDate: start && !Number.isNaN(start.getTime()) ? start : null,
      startDateLabel:
        start && !Number.isNaN(start.getTime())
          ? this.formatJakartaDateTime(start)
          : '—',
      location: typeof event['location'] === 'string' && event['location'].trim() ? event['location'] : '—',
      lastModifiedLabel: this.lastModifiedLabel(event),
      categoryLabel,
      categoryId: categoryId !== null && categoryId !== undefined ? String(categoryId) : '',
      status: typeof event['status'] === 'string' ? event['status'] : '',
      eventType: typeof event['event_type'] === 'string' ? event['event_type'] : 'single',
      imageUrl: imageUrl || 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="144" height="96"><rect width="100%" height="100%" fill="%23dbeafe"/><text x="50%" y="52%" dominant-baseline="middle" text-anchor="middle" fill="%23334155" font-size="14">No Image</text></svg>'
    };
  }

  private extractFileId(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object' && 'id' in value) {
      const id = (value as { id?: unknown }).id;
      if (typeof id === 'string') return id;
    }
    return '';
  }

  private lastModifiedLabel(event: DirectusRecord): string {
    const raw = event['date_updated'] ?? event['created_at'] ?? event['date_created'];
    if (typeof raw !== 'string') return '—';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '—';
    return this.formatJakartaDateTime(date);
  }

  private formatJakartaDateTime(date: Date): string {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: this.displayTimezone
    });
  }

  applyFilters(): void {
    const term = this.search.trim().toLowerCase();
    const todayJakartaStart = this.getJakartaDayStartUtc();

    this.filteredRows = this.rows.filter((row) => {
      if (this.eventStatusFilter === 'upcoming' && (!row.startDate || row.startDate.getTime() < todayJakartaStart.getTime())) return false;
      if (this.eventStatusFilter === 'past' && (!row.startDate || row.startDate.getTime() >= todayJakartaStart.getTime())) return false;
      if (this.eventStatusFilter === 'canceled' && row.status.toLowerCase() !== 'cancelled' && row.status.toLowerCase() !== 'canceled') return false;
      if (this.categoryFilter !== 'all' && row.categoryId !== this.categoryFilter) return false;
      if (this.eventTypeFilter === 'recurring' && row.eventType.toLowerCase() !== 'recurring') return false;
      if (this.eventTypeFilter === 'single' && row.eventType.toLowerCase() === 'recurring') return false;
      if (!term) return true;
      return (
        row.name.toLowerCase().includes(term) ||
        row.location.toLowerCase().includes(term) ||
        row.categoryLabel.toLowerCase().includes(term)
      );
    });
  }

  private getJakartaDayStartUtc(): Date {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: this.displayTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const year = Number(values['year']);
    const month = Number(values['month']);
    const day = Number(values['day']);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }
    return new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00+07:00`);
  }

  clearFilters(): void {
    this.eventStatusFilter = 'all';
    this.eventTypeFilter = 'all';
    this.categoryFilter = 'all';
    this.search = '';
    this.applyFilters();
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.eventStatusFilter !== 'all') count += 1;
    if (this.eventTypeFilter !== 'all') count += 1;
    if (this.categoryFilter !== 'all') count += 1;
    return count;
  }

  get categoryFilterLabel(): string {
    if (this.categoryFilter === 'all') return 'All';
    return this.categoryOptions.find((opt) => opt.id === this.categoryFilter)?.name || this.categoryFilter;
  }

  get visibleColumns(): Array<{ key: string; label: string; visible: boolean }> {
    return this.columns.filter((col) => col.visible);
  }

  get filteredCustomizeColumns(): Array<{ key: string; label: string; visible: boolean }> {
    const term = this.columnSearch.trim().toLowerCase();
    if (!term) return this.columns;
    return this.columns.filter((col) => col.label.toLowerCase().includes(term));
  }

  toggleColumn(key: string): void {
    const target = this.columns.find((col) => col.key === key);
    if (!target || target.key === 'name') return;
    target.visible = !target.visible;
  }

  resetColumns(): void {
    this.columns = this.columns.map((col) => {
      if (col.key === 'status') return { ...col, visible: false };
      return { ...col, visible: true };
    });
  }

  closePanels(): void {
    this.filterOpen = false;
    this.customizeOpen = false;
  }

  openEventWizard(): void {
    void this.router.navigateByUrl('/events/new');
  }

  openEvent(id: string): void {
    if (!id) return;
    void this.router.navigateByUrl(`/events/${encodeURIComponent(id)}/overview`);
  }

  toggleEventMenu(id: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.openEventMenuId === id) {
      this.openEventMenuId = null;
      return;
    }
    const trigger = event.currentTarget as HTMLElement | null;
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      const estimatedMenuHeight = 110;
      const estimatedMenuWidth = 150;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      this.openEventMenuDirection = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow ? 'up' : 'down';
      this.eventMenuTop =
        this.openEventMenuDirection === 'up'
          ? Math.max(8, rect.top - estimatedMenuHeight - 6)
          : Math.min(window.innerHeight - estimatedMenuHeight - 8, rect.bottom + 6);
      this.eventMenuLeft = Math.max(8, Math.min(window.innerWidth - estimatedMenuWidth - 8, rect.right - estimatedMenuWidth));
    } else {
      this.openEventMenuDirection = 'down';
      this.eventMenuTop = 8;
      this.eventMenuLeft = 8;
    }
    this.openEventMenuId = id;
  }

  async duplicateAsDraft(row: EventRow): Promise<void> {
    try {
      const nowIso = new Date().toISOString();
      await this.directus.create('events', {
        title: `${row.name} (copy)`,
        status: 'draft',
        event_type: row.eventType || 'single',
        location: row.location === '—' ? null : row.location,
        start_date: row.startDate ? row.startDate.toISOString() : nowIso,
        eventcategory_id: row.categoryId || null
      });
      this.openEventMenuId = null;
      await this.loadRows();
    } catch {
      this.error = 'Failed to duplicate event as draft.';
    }
  }

  async cancelEvent(row: EventRow): Promise<void> {
    this.pendingConfirmAction = 'cancel-event';
    this.pendingConfirmEventRow = row;
    this.confirmDialogTitle = 'Cancel event?';
    this.confirmDialogMessage = `Are you sure you want to cancel "${row.name}"? Guests will no longer be able to register.`;
    this.confirmDialogConfirmLabel = 'Cancel Event';
    this.confirmDialogOpen = true;
  }

  private async performCancelEvent(row: EventRow): Promise<void> {
    try {
      await this.directus.update('events', row.id, { status: 'canceled' });
      this.openEventMenuId = null;
      await this.loadRows();
    } catch {
      this.error = 'Failed to cancel event.';
    }
  }

  async deleteEvent(row: EventRow): Promise<void> {
    this.pendingConfirmAction = 'delete-event';
    this.pendingConfirmEventRow = row;
    this.confirmDialogTitle = 'Delete event?';
    this.confirmDialogMessage = `Delete "${row.name}"? This action cannot be undone.`;
    this.confirmDialogConfirmLabel = 'Delete Event';
    this.confirmDialogOpen = true;
  }

  private async performDeleteEvent(row: EventRow): Promise<void> {
    try {
      await this.directus.remove('events', row.id);
      this.openEventMenuId = null;
      await this.loadRows();
    } catch {
      this.error = 'Failed to delete event.';
    }
  }

  closeConfirmDialog(): void {
    if (this.confirmDialogBusy) return;
    this.confirmDialogOpen = false;
    this.pendingConfirmAction = null;
    this.pendingConfirmEventRow = null;
  }

  async confirmDialogProceed(): Promise<void> {
    if (!this.pendingConfirmAction || !this.pendingConfirmEventRow || this.confirmDialogBusy) return;
    this.confirmDialogBusy = true;
    try {
      if (this.pendingConfirmAction === 'cancel-event') {
        await this.performCancelEvent(this.pendingConfirmEventRow);
      } else if (this.pendingConfirmAction === 'delete-event') {
        await this.performDeleteEvent(this.pendingConfirmEventRow);
      }
      this.confirmDialogOpen = false;
      this.pendingConfirmAction = null;
      this.pendingConfirmEventRow = null;
    } finally {
      this.confirmDialogBusy = false;
    }
  }

  private async safeList(
    collection: string,
    query: Record<string, string | number | boolean | undefined>
  ): Promise<DirectusRecord[]> {
    try {
      return await this.directus.list(collection, query);
    } catch {
      return [];
    }
  }
}

@Component({
  standalone: true,
  imports: [EventsListViewComponent],
  template: `<app-events-list-view heading="Published" status="published" [defaultUpcomingOnly]="true" />`,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
    `
  ]
})
export class EventsComponent {}

@Component({
  standalone: true,
  imports: [EventsListViewComponent],
  template: `<app-events-list-view heading="Drafts" status="draft" [defaultUpcomingOnly]="false" />`,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
    `
  ]
})
export class EventsDraftComponent {}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="category-page">
      <header class="category-top">
        <div>
          <h1>Categories</h1>
          <p>Display events on your site by category and filter events on your dashboard.</p>
        </div>
        <div class="actions">
          <button type="button" class="icon-circle" aria-label="sparkle">✦</button>
          <button type="button" class="btn btn-primary" (click)="openCreateModal()">+ New Category</button>
        </div>
      </header>

      <section class="category-table">
        <p *ngIf="loading" class="state">Loading categories...</p>
        <p *ngIf="error" class="state error">{{ error }}</p>
        <p *ngIf="!loading && !error && rows.length === 0" class="state">No categories yet.</p>

        <div *ngIf="!loading && !error && rows.length > 0">
          <article
            class="category-row"
            *ngFor="let row of rows"
            (mouseenter)="rowHoverId = row.id"
            (mouseleave)="rowHoverId = ''"
          >
            <div class="category-name">{{ row.name }}</div>
            <div class="category-meta">
              <span class="meta-label">Connected events</span>
              <span class="meta-value">
                {{ row.publishedCount }} published<span *ngIf="row.draftCount > 0"> · {{ row.draftCount }} drafts</span>
              </span>
            </div>
            <div class="category-actions">
              <button
                type="button"
                class="edit-btn"
                [class.visible]="rowHoverId === row.id"
                (mouseenter)="rowHoverId = row.id"
                (click)="openEditModal(row)"
              >
                Edit
              </button>
              <div class="menu-wrap" (mouseenter)="rowHoverId = row.id">
                <button type="button" class="icon-btn" (click)="toggleMenu(row.id, $event)">…</button>
                <div class="row-menu" *ngIf="openMenuId === row.id" (click)="$event.stopPropagation()">
                  <button type="button" class="danger" (click)="deleteCategory(row)">Delete</button>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <div class="modal-backdrop" *ngIf="modalOpen" (click)="closeModal()">
        <div class="modal-card" (click)="$event.stopPropagation()">
          <header>
            <h3>{{ editId ? 'Edit Category' : 'New Category' }}</h3>
            <button type="button" class="close-btn" (click)="closeModal()">×</button>
          </header>
          <label>
            Name
            <input type="text" [(ngModel)]="form.name" />
          </label>
          <label>
            Slug
            <input type="text" [(ngModel)]="form.slug" />
          </label>
          <label>
            Description
            <textarea rows="3" [(ngModel)]="form.description"></textarea>
          </label>
          <div class="modal-actions">
            <button type="button" class="btn" (click)="closeModal()">Cancel</button>
            <button type="button" class="btn btn-primary" (click)="saveCategory()" [disabled]="saving">
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
      .category-page {
        display: grid;
        gap: 14px;
        width: 100%;
        max-width: 1320px;
        margin: 0;
      }
      .category-top {
        display: flex;
        justify-content: space-between;
        align-items: end;
        gap: 16px;
      }
      .category-top h1 {
        margin: 0;
        font-size: 34px;
        line-height: 1;
        letter-spacing: -0.02em;
      }
      .category-top p {
        margin: 8px 0 0;
        color: #4b5d79;
        font-size: 18px;
        line-height: 1.3;
      }
      .actions {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .icon-circle {
        width: 36px;
        height: 36px;
        border-radius: 999px;
        border: 1px solid #b9d0ee;
        background: #fff;
        color: #0097d7;
      }
      .btn {
        border: 1px solid #b9d0ee;
        background: #fff;
        color: #1d3b8f;
        border-radius: 999px;
        padding: 8px 16px;
        font-weight: 700;
      }
      .btn.btn-primary {
        background: #0097d7;
        border-color: #0097d7;
        color: #fff;
      }
      .category-table {
        border: 1px solid #cad7ea;
        border-radius: 14px;
        overflow: hidden;
        background: #fff;
        box-shadow: 0 4px 14px rgba(18, 31, 51, 0.05);
      }
      .state {
        margin: 0;
        padding: 16px;
        color: #4b5d79;
      }
      .state.error {
        color: #bf1f2f;
      }
      .category-row {
        display: grid;
        grid-template-columns: minmax(280px, 1.6fr) minmax(240px, 1fr) auto;
        align-items: center;
        gap: 16px;
        padding: 12px 20px;
        border-bottom: 1px solid #d8e1ef;
        transition: background-color 0.15s ease;
      }
      .category-row:last-child {
        border-bottom: none;
      }
      .category-row:hover {
        background: #d9e7fb;
      }
      .category-name {
        font-size: 1rem;
        line-height: 1.2;
        font-weight: 700;
        letter-spacing: 0;
      }
      .category-meta {
        display: grid;
        gap: 3px;
      }
      .meta-label {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #678;
        font-weight: 700;
      }
      .meta-value {
        color: #304563;
        font-size: 1rem;
        font-weight: 600;
      }
      .category-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .edit-btn {
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.15s ease;
        border: 1px solid #8fcde7;
        background: #fff;
        color: #0097d7;
        border-radius: 999px;
        padding: 7px 18px;
        font-weight: 700;
      }
      .edit-btn.visible {
        opacity: 1;
        pointer-events: auto;
      }
      .menu-wrap {
        position: relative;
      }
      .icon-btn {
        width: 32px;
        height: 32px;
        border-radius: 999px;
        border: 1px solid #b9d0ee;
        background: #fff;
        color: #0097d7;
        font-size: 20px;
        line-height: 1;
      }
      .icon-btn:hover,
      .edit-btn.visible:hover {
        filter: brightness(0.96);
      }
      .row-menu {
        position: absolute;
        right: 0;
        top: calc(100% + 6px);
        min-width: 140px;
        background: #fff;
        border: 1px solid #cad7ea;
        border-radius: 10px;
        box-shadow: 0 10px 24px rgba(10, 25, 51, 0.12);
        z-index: 40;
      }
      .row-menu button {
        width: 100%;
        text-align: left;
        background: transparent;
        border: none;
        padding: 10px 14px;
      }
      .row-menu .danger {
        color: #cc1f1a;
      }
      .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(18, 31, 51, 0.4);
        display: grid;
        place-items: center;
        z-index: 80;
      }
      .modal-card {
        width: min(560px, calc(100vw - 32px));
        background: #fff;
        border: 1px solid #cad7ea;
        border-radius: 14px;
        padding: 16px;
        display: grid;
        gap: 10px;
      }
      .modal-card header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .modal-card h3 {
        margin: 0;
      }
      .close-btn {
        border: none;
        background: transparent;
        font-size: 24px;
      }
      .modal-card label {
        display: grid;
        gap: 6px;
        font-weight: 600;
      }
      .modal-card input,
      .modal-card textarea {
        border: 1px solid #b9d0ee;
        border-radius: 8px;
        padding: 10px 12px;
      }
      .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
      }
      @media (max-width: 900px) {
        .category-top h1 {
          font-size: 30px;
        }
        .category-top p {
          font-size: 16px;
        }
        .category-row {
          grid-template-columns: 1fr;
          gap: 8px;
        }
        .category-name {
          font-size: 22px;
        }
      }
    `
  ]
})
export class EventCategoriesComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  rows: Array<{
    id: string;
    name: string;
    slug: string;
    description: string;
    publishedCount: number;
    draftCount: number;
  }> = [];
  rowHoverId = '';
  openMenuId = '';
  modalOpen = false;
  editId = '';
  form = { name: '', slug: '', description: '' };

  constructor(private readonly directus: DirectusService) {}

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      const [categories, events] = await Promise.all([
        this.directus.list('eventcategories', { fields: 'id,name,slug,description', sort: 'name', limit: 500 }),
        this.directus.list('events', { fields: 'id,eventcategory_id,status', limit: 2000 })
      ]);
      const counts = new Map<string, { published: number; draft: number }>();
      for (const evt of events) {
        const categoryIdRaw = evt['eventcategory_id'];
        if (categoryIdRaw === null || categoryIdRaw === undefined || categoryIdRaw === '') continue;
        const categoryId = String(categoryIdRaw);
        if (!counts.has(categoryId)) counts.set(categoryId, { published: 0, draft: 0 });
        const status = String(evt['status'] || '').toLowerCase();
        if (status === 'published') counts.get(categoryId)!.published += 1;
        if (status === 'draft') counts.get(categoryId)!.draft += 1;
      }
      this.rows = categories.map((row) => {
        const id = String(row['id'] ?? '');
        const c = counts.get(id) || { published: 0, draft: 0 };
        return {
          id,
          name: String(row['name'] ?? 'Untitled'),
          slug: String(row['slug'] ?? ''),
          description: String(row['description'] ?? ''),
          publishedCount: c.published,
          draftCount: c.draft
        };
      });
    } catch {
      this.error = 'Failed to load categories.';
      this.rows = [];
    } finally {
      this.loading = false;
    }
  }

  openCreateModal(): void {
    this.editId = '';
    this.form = { name: '', slug: '', description: '' };
    this.modalOpen = true;
    this.openMenuId = '';
  }

  openEditModal(row: { id: string; name: string; slug: string; description: string }): void {
    this.editId = row.id;
    this.form = { name: row.name, slug: row.slug, description: row.description };
    this.modalOpen = true;
    this.openMenuId = '';
  }

  closeModal(): void {
    this.modalOpen = false;
    this.saving = false;
  }

  toggleMenu(id: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openMenuId = this.openMenuId === id ? '' : id;
  }

  async saveCategory(): Promise<void> {
    const name = this.form.name.trim();
    if (!name || this.saving) return;
    this.saving = true;
    const slug = (this.form.slug || this.form.name)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    try {
      if (this.editId) {
        await this.directus.update('eventcategories', this.editId, {
          name,
          slug,
          description: this.form.description.trim() || null
        });
      } else {
        await this.directus.create('eventcategories', {
          name,
          slug,
          description: this.form.description.trim() || null
        });
      }
      this.closeModal();
      await this.load();
    } catch {
      this.error = this.editId ? 'Failed to update category.' : 'Failed to create category.';
      this.saving = false;
    }
  }

  async deleteCategory(row: { id: string; name: string }): Promise<void> {
    this.openMenuId = '';
    const confirmed = window.confirm(`Delete category "${row.name}"?`);
    if (!confirmed) return;
    try {
      await this.directus.remove('eventcategories', row.id);
      await this.load();
    } catch {
      this.error = 'Failed to delete category.';
    }
  }
}
